import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { TokenInvalidErrorBody } from "@/shared/types/auth";
import AuthService from "@/shared/services/AuthService";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    __isRefreshRequest?: boolean;
  }
}

const ACCESS_TOKEN_KEY = "access_token";

const $api = axios.create({
  baseURL:
    import.meta.env.VITE_API_URL + import.meta.env.VITE_APP_VERSION ||
    "http://localhost:3001/v1/web",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 10000,
});

const $apiAuth = axios.create({
  baseURL:
    import.meta.env.VITE_API_URL + import.meta.env.VITE_APP_VERSION ||
    "http://localhost:3001/v1/auth",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

$apiAuth.defaults.withCredentials = false;
$apiAuth.defaults.withXSRFToken = true;
$apiAuth.defaults.xsrfCookieName = "csrftoken";
$apiAuth.defaults.xsrfHeaderName = "X-CSRFToken";

$api.defaults.withCredentials = true;
$api.defaults.withXSRFToken = true;
$api.defaults.xsrfCookieName = "csrftoken";
$api.defaults.xsrfHeaderName = "X-CSRFToken";

const setAuthHeaderFromToken = (token: string | null) => {
  if (token) {
    $api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete $api.defaults.headers.common.Authorization;
  }
};

const getAccessTokenFromStorage = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
};

const initAuthFromStorage = () => {
  const token = getAccessTokenFromStorage();
  if (token) {
    setAuthHeaderFromToken(token);
  }
};

initAuthFromStorage();

$apiAuth.interceptors.request.use(
  (config) => {
    if (config.headers) {
      config.headers["X-Requested-With"] = "XMLHttpRequest";
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);
// Интерцептор для обработки запросов
$api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.headers) {
      config.headers["X-Requested-With"] = "XMLHttpRequest";
    }
    // Refresh-запрос не должен отправлять Bearer (протухший access)
    if (config.__isRefreshRequest && config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Очередь запросов, ожидающих повторной отправки после refresh
let isRefreshing = false;
type QueuedRequest = {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
};
const failedQueue: QueuedRequest[] = [];

const SESSION_EXPIRED_EVENT = "auth:sessionExpired";

function isTokenInvalidError(data: unknown): data is TokenInvalidErrorBody {
  if (!data || typeof data !== "object") return false;
  const d = data as TokenInvalidErrorBody;
  if (d.detail?.code === "token_not_valid") return true;
  if (d.code?.code === "token_not_valid") return true;
  const msg = d.messages?.[0]?.message?.code ?? d.messages?.[0]?.token_type?.code;
  return msg === "token_not_valid";
}

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ config, resolve, reject }) => {
    if (error || !token) {
      reject(error ?? new Error("Session expired"));
    } else {
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      $api.request(config).then(resolve).catch(reject);
    }
  });
  failedQueue.length = 0;
  isRefreshing = false;
}

// Интерцептор: при 401 token_not_valid — refresh и повтор запросов
$api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig | undefined;

    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    if (originalRequest.__isRefreshRequest) {
      return Promise.reject(error);
    }

    const data = error.response?.data;
    if (!isTokenInvalidError(data)) {
      return Promise.reject(error);
    }

    const promise = new Promise<unknown>((resolve, reject) => {
      failedQueue.push({
        config: originalRequest,
        resolve,
        reject,
      });
    });

    if (!isRefreshing) {
      isRefreshing = true;
      AuthService.refreshToken()
        .then((newAccess) => {
          processQueue(null, newAccess);
        })
        .catch((refreshError) => {
          AuthService.clearTokens();
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
          }
          processQueue(refreshError ?? new Error("Session expired"), null);
        });
    }

    return promise as Promise<unknown>;
  },
);

export {
  $api,
  $apiAuth,
  ACCESS_TOKEN_KEY,
  getAccessTokenFromStorage,
  setAuthHeaderFromToken,
};
